import { createContext, useContext, useMemo, useReducer, useRef } from 'react';
import { useWindows } from '../components/Windows';
import { useAuthentication, type AuthenticationService } from './AuthService';
import { useConversation, type ConversationState } from './ConversationService';

export type TargetLanguage = 'swift' | 'css';
export type CodeSource = 'colors' | 'typography_styles';
type LoadingState =
  | {
      isLoading: true;
      loadingSource: CodeSource;
      loadingTargetLanguage: TargetLanguage;
    }
  | {
      isLoading: false;
      loadingSource: undefined;
      loadingTargetLanguage: undefined;
    };

type ErrorState = {
  errorMessage: string | null;
};

export type CodeState =
  | ({
      type: 'initial';
      source?: CodeSource;
    } & LoadingState &
      ErrorState)
  | ({
      type: 'displaying-code';
      source: CodeSource;
      targetLanguage: TargetLanguage;
      code: string;
    } & LoadingState &
      ErrorState);

type CodeService = {
  generateCode: ({
    source,
    targetLanguage,
  }: {
    source: CodeSource;
    targetLanguage: TargetLanguage;
  }) => void;
};

export type CodeAction =
  | { type: 'setSource'; source?: CodeSource }
  | { type: 'start-load'; source: CodeSource; targetLanguage: TargetLanguage }
  | {
      type: 'finish-load';
      source: CodeSource;
      targetLanguage: TargetLanguage;
      code: string;
    }
  | { type: 'fail-load'; errorMessage: string };
export type CodeDispatch = (_: CodeAction) => void;
export type CodeDispatchHelpers = {
  showSource: (_: { source: CodeSource }) => void;
  hide: () => void;
  toggle: (_: { source: CodeSource }) => void;
};

export type CodeContextProps = {
  state: CodeState;
  dispatchHelpers: CodeDispatchHelpers;
  service: CodeService;
};
const CodeContext = createContext<CodeContextProps | null>(null);

export const GenerateCodeErrorCopy =
  'Unable to generate code right now. Please try again.';

export function codeReducer(state: CodeState, action: CodeAction): CodeState {
  switch (action.type) {
    case 'setSource': {
      if (state.type === 'displaying-code') {
        if (state.source === action.source) {
          return state;
        }

        return {
          type: 'initial',
          source: action.source,
          isLoading: false,
          loadingSource: undefined,
          loadingTargetLanguage: undefined,
          errorMessage: null,
        };
      }

      return {
        type: 'initial',
        source: action.source,
        isLoading: false,
        loadingSource: undefined,
        loadingTargetLanguage: undefined,
        errorMessage: null,
      };
    }
    case 'finish-load': {
      if (
        !state.isLoading ||
        state.loadingSource !== action.source ||
        state.loadingTargetLanguage !== action.targetLanguage
      ) {
        // No-op
        return state;
      }
      return {
        ...state,
        type: 'displaying-code',
        source: action.source,
        targetLanguage: action.targetLanguage,
        code: action.code,
        isLoading: false,
        loadingSource: undefined,
        loadingTargetLanguage: undefined,
        errorMessage: null,
      };
    }
    case 'start-load': {
      return {
        ...state,
        isLoading: true,
        loadingSource: action.source,
        loadingTargetLanguage: action.targetLanguage,
        errorMessage: null,
      };
    }
    case 'fail-load': {
      return {
        ...state,
        isLoading: false,
        loadingSource: undefined,
        loadingTargetLanguage: undefined,
        errorMessage: action.errorMessage,
      };
    }
  }
}

export const initialCodeState: CodeState = {
  type: 'initial',
  isLoading: false,
  loadingSource: undefined,
  loadingTargetLanguage: undefined,
  errorMessage: null,
};

export function useCode(): CodeContextProps {
  const props = useContext(CodeContext);
  if (props === null) {
    throw new Error('`useCode` must be used within a CodeBoundary');
  }
  return props;
}

export function CodeBoundary({ children }: React.PropsWithChildren) {
  const { service: authService } = useAuthentication();
  const { state: conversationState } = useConversation();
  const { dispatch: windowDispatch } = useWindows();

  const [state, dispatch] = useReducer(codeReducer, initialCodeState);
  const dispatchHelpers: CodeDispatchHelpers = {
    showSource: function ({ source }: { source: CodeSource }) {
      windowDispatch({ type: 'show', id: 'code' });
      dispatch({ type: 'setSource', source });
    },
    hide: function () {
      windowDispatch({ type: 'hide', id: 'code' });
      dispatch({ type: 'setSource', source: undefined });
    },
    toggle: function ({ source }: { source: CodeSource }) {
      if (state.source === undefined || state.source !== source) {
        dispatchHelpers.showSource({ source });
      } else {
        dispatchHelpers.hide();
      }
    },
  };

  const stateRef = useRef<CodeState>(state);
  const conversationStateRef = useRef<ConversationState>(conversationState);
  const authServiceRef = useRef<AuthenticationService>(authService);
  stateRef.current = state;
  conversationStateRef.current = conversationState;
  authServiceRef.current = authService;

  const service: CodeService = useMemo(() => {
    return {
      generateCode: async function ({ source, targetLanguage }) {
        const state = stateRef.current;
        const conversationState = conversationStateRef.current;
        const authService = authServiceRef.current;

        const alreadyDisplayingSource =
          state.type === 'displaying-code' &&
          state.source === source &&
          state.targetLanguage === targetLanguage &&
          state.errorMessage === null;
        if (alreadyDisplayingSource) {
          // Only generate a different source
          return;
        }

        const alreadyLoadingSource =
          state.isLoading &&
          state.loadingSource === source &&
          state.loadingTargetLanguage === targetLanguage;
        if (alreadyLoadingSource) {
          return;
        }

        if (conversationState.type !== 'conversation') {
          return;
        }

        dispatch({ type: 'start-load', source, targetLanguage });
        try {
          const { code } = await authService.sendRequest({
            endpoint: 'generate_code',
            method: 'POST',
            bodyObj: {
              source,
              conversationID: conversationState.conversationID,
              targetLanguage,
            },
            requiresAuth: true,
          });
          dispatch({ type: 'finish-load', source, targetLanguage, code });
        } catch (error) {
          console.error('Failed to generate code', error);
          dispatch({
            type: 'fail-load',
            errorMessage: GenerateCodeErrorCopy,
          });
        }
      },
    };
  }, []);

  return (
    <CodeContext.Provider value={{ state, dispatchHelpers, service }}>
      {children}
    </CodeContext.Provider>
  );
}
